import type { AccountSecurityResponse } from '@platewell/schema';
import { useCallback, useEffect, useState } from 'react';
import { accountSecurity } from '../../lib/api.js';

export interface AccountSecurityState {
  data: AccountSecurityResponse | null;
  loading: boolean;
  /** Set when the request failed — almost always because the device is offline. */
  failed: boolean;
  refresh: () => void;
}

/**
 * The password and device facts, fetched once per visit to the Security tab.
 *
 * One request rather than three: whether the account has a password, which providers it
 * has, and what is signed in are the same question asked three ways, and the screen is
 * useless until all of them are answered.
 */
export function useAccountSecurity(enabled: boolean): AccountSecurityState {
  const [data, setData] = useState<AccountSecurityResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    void accountSecurity()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, nonce]);

  return { data, loading, failed, refresh };
}
